import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import FileUpload from '../components/FileUpload';
import Summary from '../components/Summary';
import CurrentDocumentChat from '../components/CurrentDocumentChat';
import Navigation from '../components/Navigation';
import { useAuth } from '../contexts/AuthContext';

export default function HomePage() {
  const { currentUser } = useAuth();
  const [summary, setSummary] = useState('');
  const [fullText, setFullText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [filename, setFilename] = useState('');

  return (
    <div className="min-h-screen" style={{ background: 'var(--bg-primary)' }}>
      <Navigation />

      <main className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-10">
        {currentUser ? (
          <>
            {/* Page header */}
            <div className="mb-8">
              <h1 className="text-3xl font-extrabold theme-text-primary">
                Document Summarizer
              </h1>
              <p className="mt-1 theme-text-secondary">
                Upload a PDF to generate an AI summary. Supports documents of any size — even 1,000+ pages.
              </p>
            </div>

            {/* Upload + Summary row */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
              {/* Upload card */}
              <div className="theme-card p-6">
                <h2 className="text-base font-semibold theme-text-primary mb-5 flex items-center gap-2">
                  <span className="flex items-center justify-center w-7 h-7 rounded-full text-sm font-bold text-white"
                    style={{ background: 'linear-gradient(135deg,#4f46e5,#7c3aed)' }}>1</span>
                  Upload your PDF
                </h2>
                <FileUpload
                  onSummaryGenerated={(s, ft) => { setSummary(s); setFullText(ft); }}
                  setIsLoading={setIsLoading}
                  onFileSelected={(f) => setFilename(f.name)}
                />
              </div>

              {/* Summary card */}
              <div className="theme-card p-6 flex flex-col">
                <h2 className="text-base font-semibold theme-text-primary mb-5 flex items-center gap-2">
                  <span className="flex items-center justify-center w-7 h-7 rounded-full text-sm font-bold text-white"
                    style={{ background: 'linear-gradient(135deg,#4f46e5,#7c3aed)' }}>2</span>
                  AI Summary
                </h2>
                {isLoading ? (
                  <div className="flex-1 flex flex-col gap-3 animate-pulse">
                    <div className="h-4 rounded-md w-3/4" style={{ background: 'var(--border-color)' }} />
                    <div className="h-4 rounded-md w-full" style={{ background: 'var(--border-color)' }} />
                    <div className="h-4 rounded-md w-5/6" style={{ background: 'var(--border-color)' }} />
                    <div className="h-4 rounded-md w-2/3" style={{ background: 'var(--border-color)' }} />
                    <p className="mt-4 text-sm theme-text-secondary text-center">
                      Processing your document — this may take a minute for large files…
                    </p>
                  </div>
                ) : summary ? (
                  <Summary summary={summary} isLoading={false} filename={filename} />
                ) : (
                  <div className="flex-1 flex flex-col items-center justify-center text-center gap-3 py-10"
                    style={{ border: '2px dashed var(--border-color)', borderRadius: '0.75rem' }}>
                    <span className="text-4xl">📋</span>
                    <p className="text-sm theme-text-secondary">Your summary will appear here after upload.</p>
                  </div>
                )}
              </div>
            </div>

            {/* Chat section — revealed after summary */}
            {fullText && (
              <div className="theme-card p-6 mb-8">
                <h2 className="text-base font-semibold theme-text-primary mb-1 flex items-center gap-2">
                  <span className="flex items-center justify-center w-7 h-7 rounded-full text-sm font-bold text-white"
                    style={{ background: 'linear-gradient(135deg,#4f46e5,#7c3aed)' }}>3</span>
                  Chat with your document
                </h2>
                <p className="text-sm theme-text-secondary mb-5 ml-9">
                  Ask any question and the AI answers using your document as context.
                </p>
                <CurrentDocumentChat fullText={fullText} />
              </div>
            )}

            {/* Footer links */}
            <div className="flex flex-wrap gap-6 justify-center text-sm">
              <Link to="/documents" className="theme-link font-medium flex items-center gap-1">
                View document history
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </Link>
              <Link to="/pricing" className="theme-link font-medium flex items-center gap-1">
                Buy more credits
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </Link>
            </div>
          </>
        ) : (
          <div className="flex flex-col items-center justify-center py-32 text-center gap-6">
            <span className="text-6xl">🔒</span>
            <h2 className="text-2xl font-bold theme-text-primary">Sign in to get started</h2>
            <p className="theme-text-secondary max-w-sm">
              Create a free account and receive 3 credits to summarize your first PDFs.
            </p>
            <Link to="/pricing" className="theme-link font-medium">
              See pricing plans →
            </Link>
          </div>
        )}
      </main>
    </div>
  );
}
